import { defineStore } from "pinia";
import { computed, ref, onScopeDispose } from "vue";
import { api } from "@/api/client.js";
import { useMonitorStore } from "@/stores/monitor.js";

const GAINERS_CACHE = "flap_gainers_listed_v4_source_time";
/** 与监听同一数据源；升 key 丢掉旧 feed 缓存 */
const LATEST_CACHE = "flap_latest_deploys_v3_mon";
const CACHE_MAX_AGE = 2 * 60 * 60 * 1000;
const GAINERS_TTL_SECONDS = 60;

function normalizeLatest(rows) {
  const seen = new Map();
  for (const row of rows || []) {
    const token = String(row?.token || "").toLowerCase();
    if (!/^0x[a-f0-9]{40}$/.test(token) || seen.has(token)) continue;
    const time = Number(row.time || row.listedAt || 0);
    const block = Number(row.block || 0);
    seen.set(token, {
      token,
      symbol: String(row.symbol || row.name || "TOKEN"),
      name: String(row.name || row.symbol || "TOKEN"),
      image: String(row.image || ""),
      factoryName: String(row.factoryName || ""),
      time: time > 1e12 ? time : time > 0 ? time * 1000 : 0,
      block,
      listed: !!(row.listed || Number(row.status) >= 2),
    });
  }
  return Array.from(seen.values())
    .sort((a, b) => (b.block || 0) - (a.block || 0) || (b.time || 0) - (a.time || 0))
    .slice(0, 36);
}

export function normalizeGainers(rows, now = Date.now()) {
  const seconds = now / 1000;
  return (Array.isArray(rows) ? rows : []).filter(r => r && typeof r === 'object')
    .map((r) => ({
      token: String(r.token || "").toLowerCase(),
      symbol: String(r.symbol || r.name || "TOKEN"),
      name: String(r.name || r.symbol || "TOKEN"),
      image: String(r.image || ""),
      change24h: Number(r.change24h ?? r.change ?? r.pct ?? NaN),
      updatedAt: Number(r.updatedAt || 0),
      stale: Boolean(r.stale) || seconds - Number(r.updatedAt || 0) >= GAINERS_TTL_SECONDS,
      source: String(r.source || ''),
      unit: String(r.unit || ''),
    }))
    .filter((r) => /^0x[a-f0-9]{40}$/.test(r.token) && Number.isFinite(r.change24h)
      && r.source === 'dexscreener' && r.unit === 'USD'
      && r.updatedAt > 0 && r.updatedAt <= seconds + 2)
    .sort((a, b) => Number(a.stale) - Number(b.stale) || b.change24h - a.change24h)
    .slice(0, 200);
}

function readCache(key, maxAge = CACHE_MAX_AGE) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.items?.length) return null;
    if (Date.now() - Number(data.updatedAt || 0) > maxAge) return null;
    return data;
  } catch {
    return null;
  }
}

function writeCache(key, items, updatedAt = Date.now()) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ updatedAt, items })
    );
  } catch {
    /* ignore */
  }
}

export const useSideRailsStore = defineStore("sideRails", () => {
  const gainers = ref([]);
  const deploys = ref([]);
  const gainersLoading = ref(false);
  const gainersUnavailable = ref(false);
  const deploysLoading = ref(false);
  const enabled = ref(true);
  // Keep the last known ranking until a nonempty valid snapshot replaces it.
  // Aging changes its label, never its source timestamp or visibility.
  const freshnessTimer = setInterval(() => {
    const current = normalizeGainers(gainers.value);
    if (current.length !== gainers.value.length || current.some((row, i) =>
      row.token !== gainers.value[i]?.token || row.stale !== gainers.value[i]?.stale)) {
      gainersUnavailable.value = true;
      gainers.value = current;
    }
  }, 1000);
  freshnessTimer.unref?.();
  onScopeDispose(() => clearInterval(freshnessTimer));

  const gainersCount = computed(() => gainers.value.length);
  const gainersStale = computed(() => gainers.value.some(row => row.stale));
  const deploysCount = computed(() => deploys.value.length);

  function hydrateFromCache() {
    const g = readCache(GAINERS_CACHE, Infinity);
    if (g?.items?.length) {
      gainers.value = normalizeGainers(g.items);
      gainersUnavailable.value = gainers.value.some(row => row.stale);
    }
    const d = readCache(LATEST_CACHE);
    if (d?.items?.length) deploys.value = normalizeLatest(d.items);
  }

  function retainGainers() {
    gainersUnavailable.value = true;
    gainers.value = normalizeGainers(gainers.value.map(row => ({ ...row, stale: true })));
    if (gainers.value.length) writeCache(GAINERS_CACHE, gainers.value,
      Math.max(...gainers.value.map(row => row.updatedAt * 1000)));
  }

  async function fetchGainers() {
    if (!enabled.value) return;
    if (gainersLoading.value) return;
    gainersLoading.value = true;
    try {
      const json = await api.gainers(200);
      const items = normalizeGainers(json.items);
      if (items.length && (json.ok === true || !gainers.value.length)) {
        // An empty or failed refresh must not erase the last known ranking.
        // Receipt time never renews the original quote observation time.
        if (json.ok !== true) for (const item of items) item.stale = true;
        gainers.value = items;
        gainersUnavailable.value = json.ok !== true || Boolean(json.stale) || items.some(row => row.stale);
        writeCache(GAINERS_CACHE, items, Math.max(0, ...items.map(row => row.updatedAt * 1000)));
      } else {
        retainGainers();
      }
    } catch {
      retainGainers();
    } finally {
      gainersLoading.value = false;
    }
  }

  function syncDeploysFromMonitor() {
    const mon = useMonitorStore();
    const items = normalizeLatest(
      (mon.rows || []).map((r) => ({
        token: r.token,
        symbol: r.symbol || r.name,
        name: r.name || r.symbol,
        image: r.image || r.logo || "",
        factoryName: r.factoryName || "",
        time: r.time,
        block: r.block,
        listed: mon.isListedRow(r),
        status: r.status,
      }))
    );
    if (items.length) {
      deploys.value = items;
      writeCache(LATEST_CACHE, items);
    }
  }

  async function fetchDeploys({ soft = true } = {}) {
    if (!enabled.value) return;
    if (deploysLoading.value) return;
    deploysLoading.value = true;
    try {
      // 与监听「最新」同一套：/api/deploys.php → mon.rows，按 block 降序
      const mon = useMonitorStore();
      await mon.fetchList({ soft });
      syncDeploysFromMonitor();
    } catch {
      if (!soft && !deploys.value.length) {
        /* keep empty */
      }
    } finally {
      deploysLoading.value = false;
    }
  }

  async function refreshAll(force = false) {
    await Promise.all([
      fetchGainers({ soft: !force }),
      fetchDeploys({ soft: !force }),
    ]);
  }

  return {
    gainers,
    deploys,
    gainersLoading,
    deploysLoading,
    enabled,
    gainersCount,
    gainersStale,
    gainersUnavailable,
    deploysCount,
    hydrateFromCache,
    fetchGainers,
    syncDeploysFromMonitor,
    fetchDeploys,
    refreshAll,
  };
});
